// src/frontend/src/components/Landing/LiveDemo.tsx
import React, { useState, useEffect, useRef } from 'react';

interface DemoMessage {
  role: 'student' | 'stakeholder';
  content: string;
  isStreaming?: boolean;
}

const LiveDemo: React.FC = () => {
  const [messages, setMessages] = useState<DemoMessage[]>([
    {
      role: 'stakeholder',
      content: "I just got out of the board meeting, and they're not happy. We promised this feature would be live by Q2, and now you're telling me we need another month? This is unacceptable."
    }
  ]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const userHasScrolled = useRef(false);
  const allowAutoScroll = useRef(true);

  const demoResponses = [
    {
      student: "I understand your frustration. The delay is due to unexpected technical complexity. Can we discuss the critical features for Q2?",
      stakeholder: "Technical complexity? That sounds like poor planning to me. We've already announced this to our customers. What exactly is so complex that your team didn't foresee?",
      coaching: "Good start by acknowledging emotions, but consider providing more specific details upfront about the technical challenges."
    },
    {
      student: "You're right to be concerned. Let me break down exactly what happened and propose a solution to minimize the impact.",
      stakeholder: "Finally, someone taking responsibility. I'm listening, but this better be good. We have customers threatening to switch to competitors.",
      coaching: "Excellent approach! Taking ownership while pivoting to solutions shows leadership and builds trust."
    },
    {
      student: "The board has every right to be upset. What if we could deliver the core functionality by Q2 and phase the remaining features?",
      stakeholder: "A phased approach? Hmm, that might work. But I need specifics - which features would be in phase one, and how does this impact our competitive advantage?",
      coaching: "Strong response! Offering a concrete alternative shows proactive problem-solving and maintains stakeholder confidence."
    }
  ];

  const simulateTyping = (text: string, callback: () => void) => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setCurrentResponse(text.slice(0, index + 1));
        index++;
        
        // Only auto-scroll during typing if user hasn't manually scrolled and auto-scroll is allowed
        if (allowAutoScroll.current && !userHasScrolled.current && chatContainerRef.current) {
          const { scrollHeight, clientHeight } = chatContainerRef.current;
          chatContainerRef.current.scrollTo({
            top: scrollHeight - clientHeight,
            behavior: 'smooth'
          });
        }
      } else {
        clearInterval(interval);
        callback();
      }
    }, 30);
  };

  const scrollToBottom = (smooth = true) => {
    if (chatContainerRef.current && allowAutoScroll.current) {
      const { scrollHeight, clientHeight } = chatContainerRef.current;
      chatContainerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  };

  const handleResponseSelect = (index: number) => {
    if (isTyping || selectedResponse !== null) return;
    
    setSelectedResponse(index);
    const response = demoResponses[index];
    
    // Reset scroll tracking and allow auto-scroll for this interaction
    userHasScrolled.current = false;
    allowAutoScroll.current = true;
    
    // Add student message
    setMessages(prev => [...prev, { role: 'student', content: response.student }]);
    
    // Scroll to show the student message after a brief delay
    setTimeout(() => {
      scrollToBottom();
    }, 100);
    
    // Start stakeholder typing after a longer delay
    setTimeout(() => {
      setIsTyping(true);
      simulateTyping(response.stakeholder, () => {
        setMessages(prev => [...prev, { 
          role: 'stakeholder', 
          content: response.stakeholder 
        }]);
        setCurrentResponse('');
        setIsTyping(false);
        
        // Allow manual scrolling again after the interaction is complete
        setTimeout(() => {
          allowAutoScroll.current = false;
        }, 1000);
      });
    }, 1500);
  };

  const resetDemo = () => {
    setMessages([messages[0]]);
    setSelectedResponse(null);
    setCurrentResponse('');
    setIsTyping(false);
    userHasScrolled.current = false;
    allowAutoScroll.current = true;
    
    // Scroll to top after reset
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Track manual scrolling with improved logic
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!allowAutoScroll.current) return;
      
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set timeout to detect if user has stopped scrolling
      scrollTimeout = setTimeout(() => {
        const { scrollHeight, clientHeight, scrollTop } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
        
        // Only mark as user scrolled if they're not near the bottom
        if (!isNearBottom) {
          userHasScrolled.current = true;
        } else {
          userHasScrolled.current = false;
        }
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  return (
    <div id="live-demo" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Experience PM Coach In Action
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Unlike generic coaching platforms, PM Coach gives you hands-on practice with 
            PM-specific scenarios and real-time feedback
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden max-w-4xl mx-auto">
          {/* Chat header */}
          <div className="bg-blue-600 text-white px-6 py-4">
            <h3 className="text-lg font-medium">Coaching Session: Timeline Pressure from Executive Sponsor</h3>
            <p className="text-blue-100 text-sm">Difficulty: Hard | Stakeholder: Sarah Chen, VP of Product</p>
          </div>

          {/* Chat messages */}
          <div 
            ref={chatContainerRef}
            className="h-96 overflow-y-auto p-6 bg-gray-50"
            style={{ scrollBehavior: 'smooth' }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`mb-4 flex ${message.role === 'student' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.role === 'student'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="mb-4 flex justify-start">
                <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-white border border-gray-200">
                  <p className="text-sm text-gray-800">{currentResponse}</p>
                  <div className="flex space-x-1 mt-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Response options */}
          <div className="border-t border-gray-200 p-6">
            {selectedResponse === null ? (
              <>
                <p className="text-sm text-gray-600 mb-4">Choose your response:</p>
                <div className="space-y-3">
                  {demoResponses.map((response, index) => (
                    <button
                      key={index}
                      onClick={() => handleResponseSelect(index)}
                      disabled={isTyping}
                      className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition duration-150 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <p className="text-sm text-gray-800">{response.student}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {/* Coaching feedback */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Real-time PM Coach Insight</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        {demoResponses[selectedResponse].coaching}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={resetDemo}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-150"
                >
                  Try Another Response
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Feature callouts */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Dynamic AI Coaching</h3>
            <p className="mt-2 text-sm text-gray-600">
              Our AI coach responds naturally with personalized guidance for every conversation
            </p>
          </div>

          <div className="text-center">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Instant Coaching Tips</h3>
            <p className="mt-2 text-sm text-gray-600">
              Get real-time feedback to improve your approach as you practice
            </p>
          </div>

          <div className="text-center">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Track Progress</h3>
            <p className="mt-2 text-sm text-gray-600">
              Monitor your improvement with detailed performance analytics and coaching reports
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveDemo;